import React, { useState, useEffect, useRef } from 'react';
import './DeleteCrateConfirmModal.css';

interface DeleteCrateConfirmModalProps {
  isOpen: boolean;
  crateName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteCrateConfirmModal: React.FC<DeleteCrateConfirmModalProps> = ({
  isOpen,
  crateName,
  onConfirm,
  onCancel
}) => {
  const [confirmationInput, setConfirmationInput] = useState('');
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCopyName = () => {
    navigator.clipboard.writeText(crateName);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (isOpen) {
      setConfirmationInput('');
      // Focus the input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirmationValid = confirmationInput === crateName;

  const handleConfirm = () => {
    if (isConfirmationValid) {
      onConfirm();
      setConfirmationInput('');
    }
  };

  const handleCancel = () => {
    setConfirmationInput('');
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isConfirmationValid) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div 
      className="delete-crate-modal-overlay"
      onClick={handleCancel}
    >
      <div 
        className="delete-crate-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="delete-crate-modal-title">Delete Data Crate</h3>
        
        <div className="delete-crate-modal-body">
          <p className="delete-crate-warning">
            ⚠️ This action cannot be undone!
          </p>
          
          <p className="delete-crate-message">
            Type the crate name to confirm deletion:
          </p>
          
          <div className="delete-crate-name-display" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong>{crateName}</strong>
            <button
              onClick={handleCopyName}
              title="Copy crate name"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '1.1rem', padding: '0.25rem 0.4rem',
                borderRadius: '4px', color: copied ? '#28a745' : '#666',
                transition: 'color 0.2s'
              }}
            >
              {copied ? '✓' : '📋'}
            </button>
          </div>
          
          <input
            ref={inputRef}
            type="text"
            className="delete-crate-input"
            placeholder="Enter crate name"
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        
        <div className="delete-crate-modal-actions">
          <button
            className="delete-crate-btn delete-crate-btn-cancel"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="delete-crate-btn delete-crate-btn-delete"
            onClick={handleConfirm}
            disabled={!isConfirmationValid}
          >
            Delete Crate
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteCrateConfirmModal;
